/**
 * Data Reliability v2 — public API.
 *
 * Single entry point: `computeDataReliability(inputs)` returns a
 * `DataReliabilityResult` with two composites (Now and Outlook), each with
 * named components, lifecycle gates, weighted-average rollup, and
 * backward-compat single composite for legacy consumers.
 *
 * Pure function — same inputs + same as_of_date = same output.
 * Retroactive reconstruction (DEV-850 trend sparkline): caller filters
 * inputs by date before each call.
 *
 * See: DEV-846 epic, DEV-847.
 */

import type {
  DataReliabilityInputs,
  DataReliabilityResult,
  ComponentResult,
} from './types';
import { DEFAULT_DATA_RELIABILITY_WEIGHTS } from './types';
import { isNowApplicable, isOutlookApplicable } from './lifecycle';
import { buildComposite, deriveLegacyComposite } from './compose';

import { computeCompleteness } from './components/completeness';
import { computeTimeliness } from './components/timeliness';
import { computeEntryLatencyTrend } from './components/entryLatencyTrend';
import { computeEngagement } from './components/engagement';
import { computeDriftRisk } from './components/driftRisk';
import { computePreflightRisk } from './components/preflightRisk';

/* ------------------------------------------------------------------------- */
/*  Re-exports                                                               */
/* ------------------------------------------------------------------------- */

export type {
  ComponentKey,
  CompositeKey,
  ComponentResult,
  CompositeResult,
  DataReliabilityResult,
  DataReliabilityInputs,
  ComponentWeights,
  ProjectMetadataInput,
  ProgressEntryInput,
  ExpectedInstanceInput,
  DigestAckInput,
  PreflightItemInput,
  PreflightItemState,
  PreflightItemPriority,
  PhaseSummaryInput,
  ReportingCadenceInput,
  Cadence,
  ProjectStatus,
} from './types';

export { DEFAULT_DATA_RELIABILITY_WEIGHTS } from './types';
export { OUTLOOK_MIN_PROJECT_AGE_DAYS } from './lifecycle';
export { isNowApplicable, isOutlookApplicable } from './lifecycle';

/* ------------------------------------------------------------------------- */
/*  Defensive weight resolution                                              */
/* ------------------------------------------------------------------------- */

/**
 * Resolve effective weights. Caller-provided weights take precedence; missing
 * fields fall back to engine defaults. Defensive against partially-filled
 * weight objects (e.g., a config row that has `now` but not `outlook`).
 */
function resolveWeights(supplied: DataReliabilityInputs['weights']) {
  return {
    now: {
      completeness:
        supplied?.now?.completeness ??
        DEFAULT_DATA_RELIABILITY_WEIGHTS.now.completeness,
      timeliness:
        supplied?.now?.timeliness ??
        DEFAULT_DATA_RELIABILITY_WEIGHTS.now.timeliness,
    },
    outlook: {
      entry_latency_trend:
        supplied?.outlook?.entry_latency_trend ??
        DEFAULT_DATA_RELIABILITY_WEIGHTS.outlook.entry_latency_trend,
      engagement:
        supplied?.outlook?.engagement ??
        DEFAULT_DATA_RELIABILITY_WEIGHTS.outlook.engagement,
      drift_risk:
        supplied?.outlook?.drift_risk ??
        DEFAULT_DATA_RELIABILITY_WEIGHTS.outlook.drift_risk,
      preflight_risk:
        supplied?.outlook?.preflight_risk ??
        DEFAULT_DATA_RELIABILITY_WEIGHTS.outlook.preflight_risk,
    },
  };
}

/* ------------------------------------------------------------------------- */
/*  Main entry point                                                         */
/* ------------------------------------------------------------------------- */

export function computeDataReliability(
  inputs: DataReliabilityInputs,
): DataReliabilityResult {
  const weights = resolveWeights(inputs.weights);

  // ── Now composite ──────────────────────────────────────────────────────
  const nowGate = isNowApplicable(inputs);
  const nowComponents: ComponentResult[] = nowGate.applicable
    ? [computeCompleteness(inputs), computeTimeliness(inputs)]
    : // When gate fails, surface placeholder components in drill-in so the
      // UI can show "not yet applicable" rather than an empty list.
      [
        {
          key: 'completeness',
          score: null,
          applicable: false,
          reason: nowGate.reason,
          explain: nowGate.reason ?? 'Not applicable',
        },
        {
          key: 'timeliness',
          score: null,
          applicable: false,
          reason: nowGate.reason,
          explain: nowGate.reason ?? 'Not applicable',
        },
      ];

  const now = buildComposite({
    key: 'now',
    components: nowComponents,
    weights: {
      completeness: weights.now.completeness,
      timeliness: weights.now.timeliness,
    },
    applicable: nowGate.applicable,
    reason: nowGate.reason,
    previousScore: inputs.previous_result?.now_score ?? null,
  });

  // ── Outlook composite ──────────────────────────────────────────────────
  const outlookGate = isOutlookApplicable(inputs);
  const outlookComponents: ComponentResult[] = outlookGate.applicable
    ? [
        computeEntryLatencyTrend(inputs),
        computeEngagement(inputs),
        computeDriftRisk(inputs),
        computePreflightRisk(inputs),
      ]
    : [
        {
          key: 'entry_latency_trend',
          score: null,
          applicable: false,
          reason: outlookGate.reason,
          explain: outlookGate.reason ?? 'Not applicable',
        },
        {
          key: 'engagement',
          score: null,
          applicable: false,
          reason: outlookGate.reason,
          explain: outlookGate.reason ?? 'Not applicable',
        },
        {
          key: 'drift_risk',
          score: null,
          applicable: false,
          reason: outlookGate.reason,
          explain: outlookGate.reason ?? 'Not applicable',
        },
        {
          key: 'preflight_risk',
          score: null,
          applicable: false,
          reason: outlookGate.reason,
          explain: outlookGate.reason ?? 'Not applicable',
        },
      ];

  const outlook = buildComposite({
    key: 'outlook',
    components: outlookComponents,
    weights: {
      entry_latency_trend: weights.outlook.entry_latency_trend,
      engagement: weights.outlook.engagement,
      drift_risk: weights.outlook.drift_risk,
      preflight_risk: weights.outlook.preflight_risk,
    },
    applicable: outlookGate.applicable,
    reason: outlookGate.reason,
    previousScore: inputs.previous_result?.outlook_score ?? null,
  });

  // ── Backward-compat composite ──────────────────────────────────────────
  const legacy = deriveLegacyComposite(now, outlook);

  // ── Summary integration ────────────────────────────────────────────────
  // Per DEV-846 lock decision: Summary uses Now only. Outlook is a forecast,
  // not a "right now" signal, so folding it into Summary would mix temporal
  // semantics. Outlook stays visible as its own signal but doesn't roll up.
  const summary_score = now.applicable ? now.score : null;
  const summary_trend = now.applicable ? now.trend : 0;

  return {
    as_of_date: inputs.as_of_date,
    project_guid: inputs.project_guid,
    now,
    outlook,
    legacy_composite_score: legacy.score,
    legacy_composite_color: legacy.color,
    summary_score,
    summary_trend,
  };
}
